import { Component } from '@theme/component';

/**
 * AutosTW custom — By-Size tyre finder.
 *
 * Cascading Width -> Profile -> Diameter dropdowns (like bobjane.com.au "By Size").
 * On submit it composes a NATIVE Shopify storefront-filter URL for the metafields
 * custom.tyre_width / custom.tyre_profile / custom.rim_diameter — no backend, no app.
 *
 * The pure functions (buildFilterUrl, cascadeOptions) carry all the logic and are unit-tested
 * with Vitest; the web component is only a thin DOM wiring layer over them.
 */

export { buildFilterUrl } from '@theme/atw-tyre-size-url';
import { buildFilterUrl } from '@theme/atw-tyre-size-url';

/**
 * Given the full list of valid [width, profile, diameter] combinations and the current
 * partial selection, return the still-valid options for the dependent dropdowns.
 * This is what makes the cascade show only real sizes (no dead-end combinations).
 *
 * @param {Array<[number, number, number]>} combos
 * @param {{width?: string|number, profile?: string|number}} sel
 * @returns {{widths: number[], profiles: number[], diameters: number[]}}
 */
export function cascadeOptions(combos, sel = {}) {
  const width = sel.width === undefined || sel.width === '' ? null : Number(sel.width);
  const profile = sel.profile === undefined || sel.profile === '' ? null : Number(sel.profile);

  const widths = new Set();
  const profiles = new Set();
  const diameters = new Set();

  for (const [w, p, d] of combos) {
    widths.add(w);
    if (width !== null && w !== width) continue;
    profiles.add(p);
    if (profile !== null && p !== profile) continue;
    diameters.add(d);
  }

  const sortNum = (a, b) => a - b;
  return {
    widths: [...widths].sort(sortNum),
    profiles: [...profiles].sort(sortNum),
    diameters: [...diameters].sort(sortNum),
  };
}

/**
 * @typedef {object} Refs
 * @property {HTMLSelectElement} width
 * @property {HTMLSelectElement} profile
 * @property {HTMLSelectElement} diameter
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLScriptElement} combos
 *
 * @extends {Component<Refs>}
 */
export class TyreSizeSelector extends Component {
  requiredRefs = ['width', 'profile', 'diameter', 'combos'];

  /** @type {Array<[number, number, number]>} */
  #combos = [];

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#combos = JSON.parse(this.refs.combos.textContent || '[]');
    } catch {
      this.#combos = [];
    }
    this.#render();
  }

  get #basePath() {
    return this.getAttribute('data-base-path') || '/collections/all';
  }

  get #selection() {
    return {
      width: this.refs.width.value,
      profile: this.refs.profile.value,
      diameter: this.refs.diameter.value,
    };
  }

  /** Repopulate dependent dropdowns from the current selection, preserving valid picks. */
  #render() {
    const { width, profile } = this.#selection;
    const { widths, profiles, diameters } = cascadeOptions(this.#combos, { width, profile });
    fillSelect(this.refs.width, widths, width);
    fillSelect(this.refs.profile, profiles, profile);
    fillSelect(this.refs.diameter, diameters, this.refs.diameter.value);
    if (this.refs.submit) this.refs.submit.disabled = !this.refs.width.value;
  }

  /** on:change="/handleChange" — a change to width/profile cascades downstream. */
  handleChange(event) {
    const changed = event?.target;
    // reset downstream picks when an upstream box changes
    if (changed === this.refs.width) {
      this.refs.profile.value = '';
      this.refs.diameter.value = '';
    } else if (changed === this.refs.profile) {
      this.refs.diameter.value = '';
    }
    this.#render();
  }

  /** on:submit="/handleSubmit" */
  handleSubmit(event) {
    event?.preventDefault();
    const url = buildFilterUrl(this.#basePath, this.#selection);
    window.location.assign(url);
  }
}

/**
 * Fill a <select> with numeric options, keeping the first (placeholder) option and
 * restoring the previously-selected value when it is still valid.
 *
 * @param {HTMLSelectElement} select
 * @param {number[]} values
 * @param {string} current
 */
function fillSelect(select, values, current) {
  const placeholder = select.querySelector('option[value=""]');
  select.textContent = '';
  if (placeholder) select.appendChild(placeholder);
  for (const value of values) {
    const option = document.createElement('option');
    option.value = `${value}`;
    option.textContent = `${value}`;
    select.appendChild(option);
  }
  select.value = values.map(String).includes(current) ? current : '';
}

if (!customElements.get('tyre-size-selector')) {
  customElements.define('tyre-size-selector', TyreSizeSelector);
}
